import type { CookieConsentValue } from "./consent";

/**
 * Region-based consent (NEXT_PUBLIC_REGIONAL_CONSENT="true"): visitors in the
 * United States, detected from the browser time zone, start with analytics and
 * ad tags on and can opt out (US state privacy laws are opt-out). Global
 * Privacy Control turns ad storage off. Everyone else, and anyone whose time
 * zone cannot place them, sees the opt-in banner: the EU, UK, Switzerland,
 * Quebec, Brazil, South Korea, China, and India all expect consent first.
 * With the flag off, every visitor sees the banner.
 */

const US_TIME_ZONE_PREFIXES = [
  "America/Indiana/",
  "America/Kentucky/",
  "America/North_Dakota/",
  "US/",
];

const US_TIME_ZONES = new Set([
  "America/New_York",
  "America/Detroit",
  "America/Chicago",
  "America/Menominee",
  "America/Denver",
  "America/Boise",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "America/Juneau",
  "America/Sitka",
  "America/Metlakatla",
  "America/Yakutat",
  "America/Nome",
  "America/Adak",
  "Pacific/Honolulu",
  // Legacy names that browsers (ICU) still report.
  "America/Indianapolis",
  "America/Fort_Wayne",
  "America/Louisville",
  "America/Knox_IN",
  "America/Shiprock",
  "America/Atka",
  "Navajo",
]);

const isUnitedStatesTimeZone = (timeZone: string): boolean =>
  US_TIME_ZONES.has(timeZone) ||
  US_TIME_ZONE_PREFIXES.some((prefix) => timeZone.startsWith(prefix));

export const isRegionalConsentEnabled = (): boolean =>
  process.env.NEXT_PUBLIC_REGIONAL_CONSENT === "true";

const readBrowserTimeZone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
};

/** True everywhere except the United States, including when the time zone is unknown. */
export const requiresConsentBanner = (
  timeZone: string | undefined = readBrowserTimeZone(),
): boolean => !timeZone || !isUnitedStatesTimeZone(timeZone);

export const hasGlobalPrivacyControl = (): boolean =>
  typeof navigator !== "undefined" &&
  (navigator as Navigator & { globalPrivacyControl?: boolean })
    .globalPrivacyControl === true;

/**
 * The consent a visitor starts with before choosing. Null means no default:
 * show the banner and keep everything off until they choose.
 */
export const getRegionalDefaultConsent = (): CookieConsentValue | null => {
  if (typeof window === "undefined") return null;
  if (!isRegionalConsentEnabled() || requiresConsentBanner()) return null;
  return hasGlobalPrivacyControl() ? "analytics" : "all";
};
